import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { hasuraClient } from '../utils/graphqlClient';
import { useNotification, useAuth } from './';
import {
  GET_ORGANIZATION_HIERARCHY_QUERY,
  GET_ORGANIZATION_HIERARCHY_BY_COMPANY_QUERY,
  GET_UNASSIGNED_USERS_QUERY,
  GET_UNASSIGNED_USERS_BY_COMPANY_QUERY,
} from '../graphql';

export const useOrganizationHierarchy = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { user } = useAuth();

  const [organizationData, setOrganizationData] = useState({
    companies: [],
    unassignedUsers: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const isRootUser = user?.role === 'ROOT';
  const userCompanyId = user?.companyId;

  const transformUserData = useCallback(
    user => ({
      id: user.id,
      name: `${user.first_name} ${user.last_name}`,
      role: user.role,
      title: user.title?.title_name || 'No Title',
      email: user.email,
      workStartDate: user.work_start_date,
      workEndDate: user.work_end_date,
    }),
    []
  );

  const transformTeamData = useCallback(
    team => ({
      id: team.id,
      name: team.name,
      description: team.description,
      manager: team.user
        ? `${team.user.first_name} ${team.user.last_name}`
        : 'No Manager',
      members: team.users?.map(transformUserData) || [],
    }),
    [transformUserData]
  );

  const transformGroupData = useCallback(
    group => ({
      id: group.id,
      name: group.name,
      description: group.description,
      manager: group.user
        ? `${group.user.first_name} ${group.user.last_name}`
        : 'No Manager',
      teams: group.teams?.map(transformTeamData) || [],
    }),
    [transformTeamData]
  );

  const transformCompanyData = useCallback(
    company => ({
      id: company.id,
      name: company.name,
      emailAddress: company.email_address_of_representative,
      representativeName: company.name_of_representative_,
      phoneNumber: company.phone_of_representative,
      groups: company.groups?.map(transformGroupData) || [],
    }),
    [transformGroupData]
  );

  const fetchOrganizationData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      let companiesData, unassignedUsersData;

      if (isRootUser) {
        const companiesResult = await hasuraClient.query(
          GET_ORGANIZATION_HIERARCHY_QUERY
        );
        companiesData = companiesResult.companies;

        const unassignedResult = await hasuraClient.query(
          GET_UNASSIGNED_USERS_QUERY
        );
        unassignedUsersData = unassignedResult.users;
      } else {
        const companiesResult = await hasuraClient.query(
          GET_ORGANIZATION_HIERARCHY_BY_COMPANY_QUERY,
          {
            companyId: userCompanyId,
          }
        );
        companiesData = companiesResult.companies;

        const unassignedResult = await hasuraClient.query(
          GET_UNASSIGNED_USERS_BY_COMPANY_QUERY,
          {
            companyId: userCompanyId,
          }
        );
        unassignedUsersData = unassignedResult.users;
      }

      const transformedCompanies = companiesData.map(transformCompanyData);
      const transformedUnassignedUsers =
        unassignedUsersData.map(transformUserData);

      setOrganizationData({
        companies: transformedCompanies,
        unassignedUsers: transformedUnassignedUsers,
      });
    } catch (error) {
      console.error('Error fetching organization hierarchy:', error);
      setError(error.message);
      showNotification(
        t('settings.organizationHierarchy.errors.fetchError'),
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    isRootUser,
    userCompanyId,
    transformCompanyData,
    transformUserData,
    t,
    showNotification,
  ]);

  const refreshData = useCallback(() => {
    fetchOrganizationData();
  }, [fetchOrganizationData]);

  useEffect(() => {
    fetchOrganizationData();
  }, [fetchOrganizationData]);

  return {
    organizationData,
    isLoading,
    error,
    refreshData,
    isRootUser,
  };
};
